<script lang="ts">
  import { onDestroy } from 'svelte';
  import { Link, navigate } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import type { Activity, Member } from '../../lib/api/types';
  import { isAuthenticated } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import { activityIcon, clockTime, length, rate } from '../../lib/utils/activities';
  import { when } from '../../lib/utils/time';
  import type { Health } from '../../lib/utils/insights';
  import { effortBadge } from '../../lib/utils/training';
  import { t } from '../../lib/i18n/index.ts';

  // Activities lists the viewer's own activities, or with groupId and userId
  // a group member's that the group may see (spec 006 FR-005, 006 FR-029);
  // each opens ActivityDetail. The owner exports the list as CSV here
  // (spec 006 FR-095) and opens Progress and Health (spec 007); importing
  // is in Settings, Activities (spec 006 FR-124, 006 FR-026); a group member sees the member's steps and active minutes when
  // shared (spec 006 FR-101).
  let { groupId, userId }: { groupId?: string; userId?: string } = $props();

  let list: Activity[] = $state([]);
  let memberName = $state('');
  let error = $state('');
  let poll: ReturnType<typeof setTimeout> | undefined;
  let own = $derived(!groupId);

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  let health = $state<Health | null>(null);

  $effect(() => {
    void groupId;
    void userId;
    load();
    health = null;
    if (groupId && userId) {
      api.get<Member[]>(`/groups/${groupId}/members`)
        .then((ms) => (memberName = ms.find((m) => m.user_id === userId)?.display_name ?? ''))
        .catch(() => {});
      // Not shared is a 404, and then nothing shows.
      api.get<Health>(`/groups/${groupId}/members/${userId}/health`).then((h) => (health = h)).catch(() => {});
    }
  });

  onDestroy(() => clearTimeout(poll));

  // load fetches the list, and again every 10 seconds while one records.
  async function load() {
    clearTimeout(poll);
    try {
      list = await api.get<Activity[]>(own ? '/me/activities' : `/groups/${groupId}/members/${userId}/activities`);
      error = '';
      if (list.some((a) => a.state !== 'finished')) {
        poll = setTimeout(load, 10_000);
      }
    } catch (e: any) {
      error = e.message;
    }
  }

  async function exportCSV() {
    try {
      await api.download('/me/activities/export.csv', 'kehai-activities.csv');
    } catch (e: any) {
      error = e.message;
    }
  }

  // MEDAL_COLORS are gold, silver, and bronze from the theme.
  const MEDAL_COLORS = ['var(--theme-yellow)', 'var(--theme-overlay2)', 'var(--theme-peach)'];

  // bestEffort is the activity's highest-ranked best effort, for its medal.
  function bestEffort(a: Activity) {
    return a.efforts?.length ? a.efforts.reduce((x, y) => (y.rank < x.rank ? y : x)) : null;
  }

  // summary leaves distance out of a timer-only sport, which never has one.
  function summary(a: Activity): string {
    return [a.gps ? (a.distance_estimated ? t('activitiesDistanceEstimated', { distance: length(a.distance_m) }) : length(a.distance_m)) : null, a.timed ? clockTime(a.moving_s) : null, a.gps && a.timed ? rate(a.type, a.distance_m, a.moving_s) : null]
      .filter(Boolean)
      .join(' · ');
  }

</script>

<div class="p-6 max-w-5xl w-full mx-auto">
  <PageHeader title={own ? t('activitiesMine') : memberName ? t('activitiesOfMember', { member: memberName }) : t('activitiesOfUnknownMember')} />
  {#if own}
    <div class="flex flex-wrap gap-2 mb-4">
      <Link to="/activities/progress" class="btn btn-secondary btn-sm inline-flex items-center gap-1"><i aria-hidden="true" class="las la-chart-line"></i>{t('activitiesProgress')}</Link>
      <Link to="/activities/health" class="btn btn-secondary btn-sm inline-flex items-center gap-1"><i aria-hidden="true" class="las la-heartbeat"></i>{t('activitiesHealth')}</Link>
      <button class="btn btn-secondary btn-sm inline-flex items-center gap-1" onclick={exportCSV}><i aria-hidden="true" class="las la-download"></i>{t('activitiesExportCsv')}</button>
    </div>
  {/if}
  {#if error}
    <p class="text-sm mb-3" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if !own && health}
    <p class="text-sm mb-4">
      <i aria-hidden="true" class="las la-shoe-prints mr-1"></i>{health.steps_any ? t('activitiesStepsToday', { n: Math.round(health.steps_today) }) : t('activitiesNoStepsYet')}
      <span class="mx-2 text-text-muted">·</span>
      <i aria-hidden="true" class="las la-heartbeat mr-1"></i>{t('activitiesActiveMinutesWeek', { n: Math.round(health.active_minutes_week) })}
    </p>
  {/if}
  <ul class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
    {#each list as a (a.id)}
      {@const best = bestEffort(a)}
      <li>
        <Link to={own ? `/activity/${a.id}` : `/activity/${groupId}/${a.id}`} class="card block w-full text-left p-3 hover:opacity-80">
          <span class="flex items-center gap-1 font-medium"><i aria-hidden="true" class="las {activityIcon(a.type)}"></i><span class="flex-1 min-w-0 truncate">{a.name}</span>
            {#if best}<i class="las la-medal" style="color: {MEDAL_COLORS[best.rank - 1]};" title={effortBadge(best)} aria-hidden="true"></i><span class="sr-only">{effortBadge(best)}</span>{/if}
          </span>
          <span class="block text-xs text-text-muted">{a.source === 'automatic' ? t('activitiesStartedAutomatic', { when: when(a.started_at) }) : when(a.started_at)}</span>
          <span class="block text-sm">{summary(a)}</span>
          {#if a.state !== 'finished'}
            <span class="block text-xs" style="color: var(--theme-accent);">{a.state === 'paused' ? t('activitiesPausedNow') : t('activitiesRecordingNow')}</span>
          {/if}
        </Link>
      </li>
    {:else}
      <li class="text-sm text-text-muted">{own ? t('activitiesNoneOwn') : t('activitiesNoneShared')}</li>
    {/each}
  </ul>
</div>
